/**
 * Typed API client for the Health Analyzer REST API.
 *
 * Responsibilities:
 *   - Inject the Cognito Bearer token (via `TokenProvider`).
 *   - Parse the standard `{ data, meta }` / `{ error, meta }` envelope.
 *   - Validate the `data` payload against a zod schema, throwing a typed
 *     `ApiError` on validation failure (and logging the first 3 zod issues to
 *     `console.warn` in dev so contract drift is loud).
 *   - Stay fully framework-agnostic so the same client runs in React Native.
 */

import type { z } from "zod";

import { ApiErrorBodySchema, apiSuccess } from "../schemas";
import type { TokenProvider, Transport } from "./transport";

export class ApiError extends Error {
  public override readonly name = "ApiError";
  public readonly status: number;
  public readonly code: string;
  public readonly details: { field: string; issue: string }[];
  public readonly requestId?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    details: { field: string; issue: string }[] = [],
    requestId?: string,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    if (requestId !== undefined) {
      this.requestId = requestId;
    }
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  transport: Transport;
  getToken: TokenProvider;
  /**
   * Optional Cognito **access token** provider. When supplied, the access
   * token is sent in an `X-Cognito-Access-Token` header alongside the
   * Authorization bearer (which stays the ID token for the API Gateway
   * authorizer). The passkeys Lambda needs the access token for Cognito's
   * WebAuthn user APIs.
   */
  getAccessToken?: TokenProvider;
  /**
   * When true, the first three zod issues for any failed response parse are
   * logged to `console.warn` so Python ↔ TypeScript contract drift is loud
   * during development. Defaults to `import.meta.env.DEV` at construction.
   */
  warnOnSchemaDrift?: boolean;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly transport: Transport;
  private readonly getToken: TokenProvider;
  private readonly getAccessToken?: TokenProvider;
  private readonly warnOnSchemaDrift: boolean;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.transport = opts.transport;
    this.getToken = opts.getToken;
    this.getAccessToken = opts.getAccessToken;
    this.warnOnSchemaDrift =
      opts.warnOnSchemaDrift ??
      (typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV));
  }

  /** GET helper. */
  async get<T>(
    path: string,
    schema: z.ZodType<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>(path, schema, { method: "GET", signal });
  }

  /** POST helper. */
  async post<T>(
    path: string,
    body: unknown,
    schema: z.ZodType<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>(path, schema, {
      method: "POST",
      body: body === undefined ? null : JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
      signal,
    });
  }

  /** PUT helper. */
  async put<T>(
    path: string,
    body: unknown,
    schema: z.ZodType<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>(path, schema, {
      method: "PUT",
      body: JSON.stringify(body ?? {}),
      headers: { "Content-Type": "application/json" },
      signal,
    });
  }

  /** PATCH helper. */
  async patch<T>(
    path: string,
    body: unknown,
    schema: z.ZodType<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>(path, schema, {
      method: "PATCH",
      body: JSON.stringify(body ?? {}),
      headers: { "Content-Type": "application/json" },
      signal,
    });
  }

  /** DELETE helper — schema is the response payload (often `null`). */
  async delete<T>(
    path: string,
    schema: z.ZodType<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>(path, schema, { method: "DELETE", signal });
  }

  private async request<T>(
    path: string,
    schema: z.ZodType<T>,
    init: {
      method: string;
      body?: string | null;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    },
  ): Promise<T> {
    const url = path.startsWith("http") ? path : `${this.baseUrl}${path}`;
    const token = await this.getToken();
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(init.headers ?? {}),
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (this.getAccessToken) {
      const accessToken = await this.getAccessToken();
      if (accessToken) headers["X-Cognito-Access-Token"] = accessToken;
    }

    const response = await this.transport.request(url, {
      method: init.method,
      headers,
      body: init.body ?? null,
      signal: init.signal,
    });

    const text = await response.text();
    let parsed: unknown = null;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new ApiError(
          response.status,
          "INVALID_JSON",
          "Server returned a non-JSON response",
        );
      }
    }

    if (!response.ok) {
      const errorParsed = ApiErrorBodySchema.safeParse(parsed);
      if (errorParsed.success) {
        const { error, meta } = errorParsed.data;
        throw new ApiError(
          response.status,
          error.code,
          error.message,
          error.details ?? [],
          meta.requestId,
        );
      }
      throw new ApiError(
        response.status,
        "HTTP_ERROR",
        `Request failed with status ${response.status}`,
      );
    }

    // 204 — no envelope to parse.
    if (response.status === 204 || parsed === null) {
      // The schema author should pass `z.unknown()` / `z.null()` for void responses.
      const result = schema.safeParse(null);
      if (result.success) return result.data;
      // If the caller expects a typed payload but the server returned 204,
      // surface that as a contract error.
      throw new ApiError(
        response.status,
        "EMPTY_BODY",
        "Server returned 204 No Content but client expected a payload",
      );
    }

    const envelopeSchema = apiSuccess(schema);
    const envelope = envelopeSchema.safeParse(parsed);
    if (envelope.success) {
      return envelope.data.data as T;
    }

    if (this.warnOnSchemaDrift) {
      this.logSchemaDrift(path, init.method, envelope.error);
    }
    throw new ApiError(
      response.status,
      "SCHEMA_MISMATCH",
      "Response failed schema validation — see console for details",
      envelope.error.issues.slice(0, 5).map((issue) => ({
        field: issue.path.join("."),
        issue: issue.message,
      })),
    );
  }

  private logSchemaDrift(
    path: string,
    method: string,
    error: z.ZodError,
  ): void {
    const head = error.issues.slice(0, 3);
    console.warn(
      `[ApiClient] schema drift on ${method} ${path} — first ${head.length} issues:`,
    );
    for (const issue of head) {
      console.warn(`  • ${issue.path.join(".") || "<root>"}: ${issue.message}`);
    }
  }
}
